import Cookies from 'js-cookie'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router'

import { CookieKeys } from '../../../../../enums/cookieKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useSegment } from '../../../../../hooks'
import { usePolling } from '../../../../../hooks/usePolling'
import { Paging } from '../../../../../types/metable'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { InvoiceDocumentType } from '../../../enums/invoiceDocumentType'
import { InvoiceStatus } from '../../../enums/invoiceStatus'
import { useFetchSalesDocuments } from '../../../hooks/useFetchSalesDocuments'
import { useHasInvoices } from '../../../hooks/useHasInvoices'
import { getInvoiceDraftEditRoute } from '../../../utils/getInvoiceDraftEditRoute'
import { getInvoiceQueryKey } from '../../../utils/getInvoiceQueryKey'
import { getTrackingDocumentType } from '../../../utils/getTrackingDocumentType'
import { InvoicesTableRow } from '../types/invoicesTableRow'
import { getInvoiceQueryData } from '../utils/getInvoiceQueryData'
import { getInvoicesTableData } from '../utils/getInvoicesTableData'
import { getTrackingEvents } from '../utils/getTrackingEvents'
import { useInvoicesList } from './invoicesList'
import { useInvoicesListFilters } from './invoicesListFilters'

interface ContextState {
  hasInvoices: boolean
  invoices: InvoicesTableRow[]
  isLoading: boolean
  onSelectRow: (invoice: InvoicesTableRow) => void
  pagination?: Paging
}

const InvoicesListDataContext = createContext<ContextState | undefined>(undefined)

interface InvoicesListDataProviderProps {
  children?: ReactNode
}

export const InvoicesListDataProvider = ({ children }: InvoicesListDataProviderProps): ReactElement => {
  const [pendingPaymentInvoiceId, setPendingPaymentInvoiceId] = useState<string>()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { track } = useSegment()
  const { organization, options, tableId } = useInvoicesList()
  const [{ customerId, page, pageSize, period, sortProperty, sortDirection, searchQuery, status }] =
    useInvoicesListFilters()
  const { hasInvoices } = useHasInvoices({ customerId, enabled: options.isFetchEnabled })

  const organizationUrl = organization?.url

  const {
    documents: invoices,
    isLoading,
    pagination,
  } = useFetchSalesDocuments({
    customerId,
    documentsListType: 'invoices',
    isEnabled: options.isFetchEnabled,
    page,
    pageSize,
    period,
    sortProperty,
    sortDirection,
    searchQuery,
    status,
  })

  useEffect(() => {
    const requestedInvoiceId = Cookies.get(CookieKeys.InvoiceViewRegisterPaymentRequest)
    if (requestedInvoiceId) {
      setPendingPaymentInvoiceId(requestedInvoiceId)
      Cookies.remove(CookieKeys.InvoiceViewRegisterPaymentRequest)
    }
  }, [])

  const isPendingPaymentSynced =
    !!pendingPaymentInvoiceId &&
    invoices.some(
      (invoice) => String(invoice.id) === pendingPaymentInvoiceId && invoice.status?.status === InvoiceStatus.Paid,
    )

  const handlePollInvoicesList = useCallback(() => {
    queryClient.invalidateQueries(QueryKeys.InvoicesZervant)
    queryClient.invalidateQueries(QueryKeys.ClientInvoicesZervant)
  }, [queryClient])

  usePolling({
    isEnabled: !!pendingPaymentInvoiceId && !isPendingPaymentSynced,
    onPoll: handlePollInvoicesList,
  })

  useEffect(() => {
    if (isPendingPaymentSynced) {
      setPendingPaymentInvoiceId(undefined)
    }
  }, [isPendingPaymentSynced])

  const invoicesData = useMemo(() => getInvoicesTableData(invoices, t), [invoices, t])

  const onSelectRow = useCallback(
    (invoice: InvoicesTableRow) => {
      const { billingStage, id: invoiceId, documentType } = invoice
      const invoiceQueryData = getInvoiceQueryData(invoiceId, invoices)
      const trackingEvents = getTrackingEvents(tableId)

      queryClient.setQueryData(getInvoiceQueryKey(invoiceId, documentType), () => invoiceQueryData)

      if (options.persistFiltersInUrl) {
        Cookies.set(CookieKeys.InvoicingInvoicesListQuery, location.search)
      }

      if (trackingEvents?.rowClicked) {
        track(trackingEvents.rowClicked, {
          documentId: invoiceId,
          documentType: getTrackingDocumentType({ billingStage, documentType }),
        })
      }

      if ([InvoiceDocumentType.Invoice, InvoiceDocumentType.CreditNote].includes(documentType)) {
        navigate(
          `${getOrganizationViewUrl(
            organizationUrl,
            reactRoute.organization.invoices.getViewInvoiceRoute(invoiceId, documentType),
          )}`,
        )
      }

      if ([InvoiceDocumentType.InvoiceDraft].includes(documentType)) {
        const editDraftRoute = getInvoiceDraftEditRoute(billingStage, invoiceId, documentType)

        navigate(`${getOrganizationViewUrl(organizationUrl, editDraftRoute)}`)
      }
    },
    [invoices, location.search, navigate, options.persistFiltersInUrl, organizationUrl, queryClient, tableId, track],
  )

  const value = useMemo(
    () => ({
      hasInvoices,
      invoices: invoicesData,
      isLoading,
      onSelectRow,
      pagination,
    }),
    [hasInvoices, invoicesData, isLoading, onSelectRow, pagination],
  )

  return <InvoicesListDataContext.Provider value={value}>{children}</InvoicesListDataContext.Provider>
}

export const useInvoicesListData = (): ContextState => {
  const context = useContext(InvoicesListDataContext)

  if (!context) {
    throw new Error('InvoicesListDataContext is missing in the module!')
  }

  return context
}
